import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "antd";
import DOMPurify from "dompurify";
import Spinner from "@/components/ui/Spinner";
import {
  showSuccess,
  showApiError,
  clearAuthStorage,
  ReactMarkdownWithGFMPlugin,
} from "@/utils";
import { formatSessionDate, toDateTimeString } from "./sandboxHelpers";
import "@/charts/TableChart.css";

const LIMIT = 10;
const SCROLL_THRESHOLD_PX = 150;

const EXPORT_FORMATS = [
  { value: "csv", label: "CSV" },
  { value: "xlsx", label: "Excel (XLSX)" },
  { value: "json", label: "JSON" },
];

// Local calendar date as YYYY-MM-DD (toISOString() would give the UTC date).
function todayDateString() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

// Local midnight of a YYYY-MM-DD string.
function localMidnight(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export default function ChatSessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const AE_BACKEND_URL = process.env.REACT_APP_AE_BACKEND_URL;

  const [sessions, setSessions] = useState([]);
  const [totalRows, setTotalRows] = useState(null);
  const [offset, setOffset] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(todayDateString());
  const [exportEndDate, setExportEndDate] = useState(todayDateString());
  const [exportFormat, setExportFormat] = useState("csv");
  const [isDownloadingExport, setIsDownloadingExport] = useState(false);

  // Result is tagged with the session it belongs to, so a stale result is never shown for a newly selected session.
  const [sessionDetailState, setSessionDetailState] = useState({
    sessionId: null,
    data: null,
    error: null,
  });
  const isCurrentDetail = sessionDetailState.sessionId === selectedSessionId;
  const sessionDetail = isCurrentDetail ? sessionDetailState.data : null;
  const sessionDetailError = isCurrentDetail ? sessionDetailState.error : null;
  const sessionDetailIsLoaded = isCurrentDetail && sessionDetail != null;

  const redirectToLogin = useCallback(() => {
    clearAuthStorage();
    localStorage.setItem(
      "redirectAfterLogin",
      location.pathname + location.search,
    );
    navigate("/login");
  }, [navigate, location]);

  useEffect(() => {
    if (!selectedSessionId) return;

    // Abort (and ignore) this request if the selection changes before it resolves.
    const controller = new AbortController();

    const fetchSessionDetail = async () => {
      try {
        const response = await fetch(
          `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/chat-sessions/${encodeURIComponent(selectedSessionId)}`,
          { credentials: "include", signal: controller.signal },
        );

        if (response.status === 401) {
          redirectToLogin();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || "Failed to load chat session details",
          );
        }

        const json = await response.json();
        if (controller.signal.aborted) return;
        setSessionDetailState({
          sessionId: selectedSessionId,
          data: json.data ?? {},
          error: null,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching chat session details:", error);
        setSessionDetailState({
          sessionId: selectedSessionId,
          data: null,
          error,
        });
      }
    };

    fetchSessionDetail();

    return () => controller.abort();
  }, [selectedSessionId, AE_BACKEND_URL, redirectToLogin]);

  // Synchronous lock: rapid scroll events can fire again before the loading state re-renders.
  const isFetchingRef = useRef(false);

  const fetchSessions = useCallback(
    async (fetchOffset, append) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (append) setIsLoadingMore(true);
      else setIsInitialLoading(true);
      setLoadError(null);

      try {
        const response = await fetch(
          `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/chat-sessions?limit=${LIMIT}&offset=${fetchOffset}`,
          { credentials: "include" },
        );

        if (response.status === 401) {
          redirectToLogin();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || "Failed to load chat sessions",
          );
        }

        const json = await response.json();
        const page = json.data ?? [];
        setSessions((prev) => {
          if (!append) return page;
          const seenIds = new Set(prev.map((s) => s.chat_session_id));
          return [...prev, ...page.filter((s) => !seenIds.has(s.chat_session_id))];
        });
        setTotalRows(json.total_rows ?? 0);
        setOffset(fetchOffset + LIMIT);
      } catch (error) {
        console.error("Error fetching chat sessions:", error);
        setLoadError(error);
      } finally {
        isFetchingRef.current = false;
        if (append) setIsLoadingMore(false);
        else setIsInitialLoading(false);
      }
    },
    [AE_BACKEND_URL, redirectToLogin],
  );

  useEffect(() => {
    fetchSessions(0, false);
    // Only run on mount - subsequent pages are triggered by scroll, not by fetchSessions identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollContainerRef = useRef(null);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el || isLoadingMore || isInitialLoading) return;
    if (totalRows != null && sessions.length >= totalRows) return;

    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX;
    if (nearBottom) {
      fetchSessions(offset, true);
    }
  };

  const handleClosePreview = () => setSelectedSessionId(null);

  const handleDownloadExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      showApiError(
        t("answerEngine.exportDateRangeRequired", "Please select a start and end date."),
      );
      return;
    }

    const startTimestamp = localMidnight(exportStartDate);
    const endTimestamp = localMidnight(exportEndDate);
    endTimestamp.setDate(endTimestamp.getDate() + 1); // end date is inclusive

    if (startTimestamp >= endTimestamp) {
      showApiError(
        t(
          "answerEngine.exportInvalidDateRange",
          "Start date must be on or before the end date.",
        ),
      );
      return;
    }

    setIsDownloadingExport(true);
    try {
      const response = await fetch(
        `${AE_BACKEND_URL}/api/v1/answer-engine-analytics/export?start_timestamp=${encodeURIComponent(startTimestamp.toISOString())}&end_timestamp=${encodeURIComponent(endTimestamp.toISOString())}&format=${exportFormat}`,
        { credentials: "include" },
      );

      if (response.status === 401) {
        redirectToLogin();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || "Failed to export Answer Engine data",
        );
      }

      const blob = await response.blob();

      const disposition = response.headers.get("Content-Disposition");
      let filename = `ae_export_${exportStartDate}_${exportEndDate}.${exportFormat}`;
      const match =
        disposition &&
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (match && match[1]) filename = match[1].replace(/['"]/g, "");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(
        t("answerEngine.exportDownloadedSuccess", "Export downloaded successfully."),
      );
      setIsExportModalOpen(false);
    } catch (error) {
      console.error("AE data export error:", error);
      showApiError(error.message || "Failed to export Answer Engine data");
    } finally {
      setIsDownloadingExport(false);
    }
  };

  return (
    <>
      <div
        className="bg-white card-radius border"
        style={{ borderColor: "var(--border)", overflow: "hidden" }}
      >
        <div className="px-3 py-2 border-bottom bg-light">
          <div className="text-sm fw-medium text-dark">
            {t("answerEngine.chatSessions", "Chat Sessions")}
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{ height: "600px", overflowY: "auto" }}
        >
          {isInitialLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <Spinner />
            </div>
          ) : loadError && sessions.length === 0 ? (
            <div className="p-3">
              <div className="alert alert-danger mb-0" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t(
                  "common.loadError",
                  "An error occurred while loading the data. Please try again later.",
                )}
              </div>
            </div>
          ) : sessions.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center text-muted"
              style={{ height: "100%" }}
            >
              <div>
                <i className="bi bi-table me-2"></i>
                {t("answerEngine.noChatSessions", "No chat sessions in this period")}
              </div>
            </div>
          ) : (
            <>
              {sessions.map((item) => (
                <div
                  key={item.chat_session_id}
                  role="button"
                  className="px-3 py-2 text-sm hoverable-row border-bottom d-flex align-items-center justify-content-between gap-3"
                  style={{ minHeight: "2.5rem", cursor: "pointer" }}
                  onClick={() => setSelectedSessionId(item.chat_session_id)}
                >
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="text-truncate">
                      {item.preview ||
                        t("answerEngine.untitledConversation", "Untitled conversation")}
                    </div>
                    <div className="small text-muted">
                      {formatSessionDate(item.created_at_timestamp)}
                      {" · "}
                      {t("answerEngine.turnCount", {
                        count: item.turn_count,
                        defaultValue: `${item.turn_count} messages`,
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                  >
                    {t("answerEngine.viewDetails", "View Details")}
                  </button>
                </div>
              ))}
              {isLoadingMore && (
                <div className="d-flex justify-content-center py-3">
                  <Spinner />
                </div>
              )}
              {loadError && !isLoadingMore && (
                <div className="d-flex justify-content-center align-items-center gap-2 py-3 text-sm text-muted">
                  <i className="bi bi-exclamation-triangle text-danger"></i>
                  {t("answerEngine.loadMoreError", "Couldn't load more sessions.")}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => fetchSessions(offset, true)}
                  >
                    {t("common.retry", "Retry")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-start mt-3">
        <button
          type="button"
          className="btn-download-report"
          onClick={() => setIsExportModalOpen(true)}
        >
          <i className="bi bi-download"></i>
          {t("answerEngine.downloadExport", "Download Raw Data")}
        </button>
      </div>

      <Modal
        open={!!selectedSessionId}
        onCancel={handleClosePreview}
        footer={null}
        width={700}
        title={t("answerEngine.chatSessionDetails", "Chat Session Details")}
      >
        {sessionDetailError ? (
          <div className="alert alert-danger mb-0" role="alert">
            {t(
              "common.loadError",
              "An error occurred while loading the data. Please try again later.",
            )}
          </div>
        ) : !sessionDetailIsLoaded ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {sessionDetail?.ai_summary && (
              <div className="alert alert-light border mb-3">
                <div className="small fw-semibold text-muted mb-1">
                  {t("answerEngine.aiSummary", "Summary")}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {sessionDetail.ai_summary}
                </div>
              </div>
            )}
            {(sessionDetail?.turns ?? []).map((turn) => (
              <div
                key={turn.chat_turn_id}
                className={`mb-3 ${turn.role === "user" ? "text-end" : "text-start"}`}
              >
                <div className="small text-muted">
                  {turn.role} · {toDateTimeString(turn.start_timestamp)}
                </div>
                <div
                  className={`d-inline-block rounded border p-2 mt-1 ${
                    turn.role === "user" ? "bg-light" : "bg-white"
                  }`}
                  style={{ textAlign: "left", maxWidth: "85%" }}
                >
                  <ReactMarkdownWithGFMPlugin>
                    {DOMPurify.sanitize(turn.content || "")}
                  </ReactMarkdownWithGFMPlugin>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={isExportModalOpen}
        onCancel={() => setIsExportModalOpen(false)}
        footer={null}
        title={t("answerEngine.downloadExport", "Download Raw Data")}
      >
        <div className="mb-3">
          <label className="form-label small text-muted" htmlFor="export-start-date">
            {t("common.startDate", "Start date")}
          </label>
          <input
            id="export-start-date"
            type="date"
            className="form-control"
            value={exportStartDate}
            max={exportEndDate || undefined}
            onChange={(e) => setExportStartDate(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label small text-muted" htmlFor="export-end-date">
            {t("common.endDate", "End date")}
          </label>
          <input
            id="export-end-date"
            type="date"
            className="form-control"
            value={exportEndDate}
            min={exportStartDate || undefined}
            onChange={(e) => setExportEndDate(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label small text-muted" htmlFor="export-format">
            {t("answerEngine.exportFormat", "Format")}
          </label>
          <select
            id="export-format"
            className="form-select"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
          >
            {EXPORT_FORMATS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn-download-report"
            onClick={handleDownloadExport}
            disabled={isDownloadingExport}
          >
            {isDownloadingExport ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("answerEngine.exportingData", "Exporting Data...")}
              </>
            ) : (
              <>
                <i className="bi bi-download"></i>
                {t("common.download", "Download")}
              </>
            )}
          </button>
        </div>
      </Modal>
    </>
  );
}
